/* iceberg-ui */
import {
    Flex,
    IconName,
    IconVariantType,
    IconWithVariant
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/* Shared */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { FontSizes } from 'application/Shared/Constants';

/* Objects */
import { Application } from 'objects/Application';

/* React */
import React from 'react';

/* Constants */
import { APP_LIST_WIDTH, CALCULATED_GRID_HEIGHT } from './constants';
import { SaasOpsActionItemStatus, SaasOpsActionItemsState } from './types';

const buildIconProps = (
    status: SaasOpsActionItemStatus,
    applicationId: string,
    isCurrentApp: boolean = false
): { variant: IconVariantType; src: IconName; 'data-testid': string } => {
    if (status === SaasOpsActionItemStatus.Completed) {
        return {
            'data-testid': `appListItem_${applicationId}_iconCompleted`,
            src: 'CheckFilled',
            variant: 'tertiary'
        };
    }

    if (status === SaasOpsActionItemStatus.Error) {
        return {
            'data-testid': `appListItem_${applicationId}_iconError`,
            src: 'CloseFilled',
            variant: 'danger'
        };
    }

    if (isCurrentApp) {
        return {
            'data-testid': `appListItem_${applicationId}_iconCurrent`,
            src: 'DualStrokeCircle',
            variant: 'tertiary'
        };
    }

    // Default to pending/skipped
    return {
        'data-testid': `appListItem_${applicationId}_iconPending`,
        src: 'CircleFilled',
        variant: 'disabled'
    };
};

export const AppsList = ({
    currentAppId,
    goToSelectedApp,
    removeApp,
    saasOpsActionItemsState
}: {
    currentAppId: string;
    goToSelectedApp: (applicationId: string) => void;
    removeApp: (applicationId: string) => void;
    saasOpsActionItemsState: SaasOpsActionItemsState;
}) => {
    const theme = useTheme();

    const nonRemovedAppState = saasOpsActionItemsState.filter(
        (appState) => appState.status !== SaasOpsActionItemStatus.Removed
    );

    // In order for the scroll height work properly in the Form section, we need the app list Grid to be rendered
    // even if it's empty.
    const singleAppDisplay = nonRemovedAppState?.length === 1;

    return (
        <Grid
            item
            width={singleAppDisplay ? 0 : APP_LIST_WIDTH}
            minWidth={singleAppDisplay ? 0 : APP_LIST_WIDTH}
            sx={{
                borderRight: singleAppDisplay
                    ? 'none'
                    : `1px solid ${theme.palette.border.pageHeader}`,
                maxHeight: CALCULATED_GRID_HEIGHT,
                minHeight: CALCULATED_GRID_HEIGHT,
                overflowY: 'auto'
            }}
        >
            {!singleAppDisplay &&
                nonRemovedAppState.map((appState) => {
                    const { recipe } = appState;
                    const { applicationId, application } = recipe;

                    const isCurrentApp =
                        appState.applicationId === currentAppId;
                    const nameToDisplay = application?.name;

                    const iconProps = buildIconProps(
                        appState.status,
                        applicationId!,
                        isCurrentApp
                    );

                    return (
                        <Application key={applicationId} data={application}>
                            <Flex
                                data-testid={`appListItem_${applicationId}`}
                                alignItems="center"
                                p={3}
                                onClick={() => goToSelectedApp(applicationId!)}
                                sx={{
                                    '&:hover': {
                                        '.showOnHover': {
                                            display: 'flex'
                                        },
                                        backgroundColor:
                                            theme.palette.saasOpsAppList
                                                .backgroundColor.hover
                                    },
                                    '.showOnHover': {
                                        display: 'none'
                                    },
                                    backgroundColor:
                                        isCurrentApp &&
                                        theme.palette.saasOpsAppList
                                            .backgroundColor.current,

                                    borderBottom: `1px solid ${theme.palette.saasOpsAppList.borderBottom}`,
                                    cursor: 'pointer',
                                    minHeight: '70px'
                                }}
                            >
                                <IconWithVariant
                                    size={FontSizes[3]}
                                    {...iconProps}
                                />
                                <Flex
                                    marginLeft={2}
                                    flexGrow="1"
                                    alignItems="center"
                                >
                                    <ApplicationNameLinkCell
                                        nameNoTruncate
                                        name={nameToDisplay}
                                    />
                                </Flex>
                                {appState.status !==
                                    SaasOpsActionItemStatus.Completed && (
                                    <Flex
                                        className="showOnHover"
                                        justifyContent="center"
                                        alignItems="center"
                                        onClick={(
                                            e: React.MouseEvent<HTMLDivElement>
                                        ) => {
                                            e.stopPropagation();
                                            removeApp(applicationId!);
                                        }}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor:
                                                    theme.palette.saasOpsAppList
                                                        .trash.backgroundColor
                                            },
                                            borderRadius: '50%',
                                            cursor: 'pointer',
                                            height: '32px',
                                            padding: '4px',
                                            transition:
                                                'background-color 0.2s ease-in-out',
                                            width: '32px'
                                        }}
                                    >
                                        <IconWithVariant
                                            size={FontSizes[3]}
                                            src="Trash"
                                            variant="tertiary"
                                        />
                                    </Flex>
                                )}
                                {/* FOR TESTING: Uncomment below to see each app's status */}
                                {/* <Flex marginLeft="20px">{appState.status}</Flex> */}
                            </Flex>
                        </Application>
                    );
                })}
        </Grid>
    );
};
